/**
 * Restore hosted-checkout form state from a persisted CheckoutProcessDto.
 */
export function mergeFormDataFromCheckoutDto(dto, prev = {}) {
  if (!dto) return prev;

  const customer = dto.customerData || {};
  const billing = customer.billingAddress || {};
  const shipping = customer.shippingAddress || {};
  const paymentMethod = normalizePaymentMethod(dto.paymentData?.paymentMethod);

  const billingAddress = {
    ...(prev.billingAddress || {}),
    firstName: billing.firstName ?? prev.billingAddress?.firstName ?? "",
    lastName: billing.lastName ?? prev.billingAddress?.lastName ?? "",
    street: billing.street ?? prev.billingAddress?.street ?? "",
    addressLine2: billing.number ?? prev.billingAddress?.addressLine2 ?? "",
    city: billing.city ?? prev.billingAddress?.city ?? "",
    zipCode: billing.zipCode ?? prev.billingAddress?.zipCode ?? "",
    state: billing.state ?? prev.billingAddress?.state ?? "",
    country: billing.country ?? prev.billingAddress?.country ?? "",
  };

  const shippingAddress = {
    ...(prev.shippingAddress || {}),
    firstName: shipping.firstName ?? prev.shippingAddress?.firstName ?? "",
    lastName: shipping.lastName ?? prev.shippingAddress?.lastName ?? "",
    street: shipping.street ?? prev.shippingAddress?.street ?? "",
    addressLine2: shipping.number ?? prev.shippingAddress?.addressLine2 ?? "",
    city: shipping.city ?? prev.shippingAddress?.city ?? "",
    zipCode: shipping.zipCode ?? prev.shippingAddress?.zipCode ?? "",
    state: shipping.state ?? prev.shippingAddress?.state ?? "",
    country: shipping.country ?? prev.shippingAddress?.country ?? "",
  };

  return {
    ...prev,
    firstName: customer.firstName ?? prev.firstName ?? "",
    lastName: customer.lastName ?? prev.lastName ?? "",
    email: customer.email ?? prev.email ?? "",
    phone: customer.phone ?? prev.phone ?? "",
    document: dto.document ?? prev.document ?? "",
    username: dto.username ?? prev.username ?? "",
    billingAddress,
    shippingAddress,
    useSameShippingAddress: prev.useSameShippingAddress ?? false,
    paymentMethod: paymentMethod ?? prev.paymentMethod,
  };
}

export function extractSelectedAddonsFromCheckoutDto(dto) {
  const items = dto?.billingData?.lineItems || [];
  return items
    .filter((li) => String(li?.type || "").toLowerCase() === "addon")
    .map((li) => ({
      itemPriceId: li.id,
      quantity: li.quantity || 1,
    }));
}

export function extractCouponCodesFromCheckoutDto(dto) {
  const codes = dto?.billingData?.couponCodes;
  return Array.isArray(codes) ? codes.filter(Boolean) : [];
}

/** Build checkout cart state from a persisted session DTO. */
export function extractCheckoutDataFromSessionDto(dto) {
  if (!dto) return null;

  const billing = dto.billingData || {};
  const payment = dto.paymentData || {};
  const lineItems = billing.lineItems || [];
  const planItem = lineItems.find(
    (li) => String(li?.type || "").toLowerCase() === "plan"
  );
  const addonItems = lineItems
    .filter((li) => String(li?.type || "").toLowerCase() === "addon")
    .map((li) => ({ itemPriceId: li.id, quantity: li.quantity || 1 }));

  return {
    billingPlatformId: billing.billingPlatformId || null,
    planId: planItem?.id || billing.planId || null,
    frequency: billing.frequency || null,
    price: payment.amount ?? null,
    currency: payment.currency || null,
    addons: addonItems,
    couponCodes: extractCouponCodesFromCheckoutDto(dto),
    forceTrialPeriodDays: billing.forceTrialPeriodDays ?? null,
    trialMetadata: billing.trialMetadata || null,
  };
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUuid(value) {
  return typeof value === "string" && UUID_RE.test(value);
}

function normalizePaymentMethod(pm) {
  if (pm == null) return null;
  if (typeof pm === "string") return pm.toLowerCase();
  return String(pm).toLowerCase();
}

export function normalizeGatewayPlatform(gateway) {
  if (gateway == null) return null;
  if (typeof gateway === "string") return gateway.toLowerCase();
  return String(gateway).toLowerCase();
}
